import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { syncToMailerLite } from "@/lib/mailerlite";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DISMISSED_KEY = "podcast-popup-dismissed";
const TRAILER_URL = "https://youtu.be/0-0xz1L9UI8";

const PodcastPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  const [showGdprDialog, setShowGdprDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem(DISMISSED_KEY) === "true") return;
    const timer = setTimeout(() => setIsOpen(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setIsOpen(false);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  const submitEmail = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert([{ email: email.trim(), source: "Podcast_Launch" }]);
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already on the list!");
          dismiss();
        } else {
          throw error;
        }
      } else {
        syncToMailerLite(email.trim(), "Podcast_Launch");
        dismiss();
        navigate("/thank-you");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!gdprConsent) {
      setShowGdprDialog(true);
      return;
    }
    await submitEmail();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in duration-300"
        onClick={dismiss}
      >
        <div
          className="relative mx-4 max-w-md w-full rounded-xl p-8 shadow-xl animate-in zoom-in-95 fade-in duration-300"
          style={{
            backgroundColor: "#0D4238",
            transform: "rotate(-1deg)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={dismiss}
            className="absolute right-3 top-3 rounded-full p-1 text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <div className="text-center space-y-4">
            <p className="text-4xl">🎧</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">
              A New Podcast for Adventurous Families
            </h2>
            <p className="font-mono text-sm text-white/80 leading-relaxed">
              <span className="font-bold text-white">Travel More Adventurously</span>
              <br />
              A new episode every Monday. Listen wherever you get your podcasts, or watch on YouTube.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2 pt-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/90 text-foreground"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="font-mono text-sm font-bold uppercase tracking-wider shadow-lg hover:scale-105 transition-all shrink-0"
                style={{ backgroundColor: "#F5A23D", color: "#0D4238" }}
              >
                {isLoading ? "Saving…" : "Send Me Episodes"}
              </Button>
            </form>

            <a
              href={TRAILER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block font-mono text-xs uppercase tracking-wider text-white/70 hover:text-white underline transition-colors"
            >
              Watch the trailer →
            </a>
          </div>
        </div>
      </div>

      <AlertDialog open={showGdprDialog} onOpenChange={setShowGdprDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-primary">Before you join…</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground leading-relaxed">
              By signing up you agree to our{" "}
              <a href="/privacy" className="text-accent underline" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>{" "}
              and to receive podcast and adventure updates. You can unsubscribe at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-mono text-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-mono text-sm"
              onClick={() => {
                setGdprConsent(true);
                submitEmail();
              }}
            >
              I Agree — Send Me Episodes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PodcastPopup;
